import React from "react"
import { useParams, useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import moment from "moment"
import { IPersonnelDocumentModel } from "@common-models/*"
import { PageTitle } from "@common-metronic/layout/core"
import MyAlert from "@common-utils/MyAlert"
import MyModal from "@common-utils/MyModal"
import MyGoogleDriveFileViewerInModal from "../../../components/MyDocumentViewer/MyGoogleDriveFileViewerInModal"
import CenteredLoader from "./_CenteredLoader"
import "./demande-embauche-print.css"
import { IPersonnelDemandeEmbaucheModel } from "../core/_models"
import { personnelDEGetDetailsRequest, personnelDEDossierPdfRequest } from "../core/_requests"
import { ROUTING_ABSOLUTE_PATH_GESTION_STRUCTURE_STAFF_DEMANDE_EMBAUCHE_EDITER, ROUTING_ABSOLUTE_PATH_GESTION_STRUCTURE_STAFF_DEMANDES_EMBAUCHE } from "../../gestion/routing/routing-endpoints"
import DemandeEmbaucheActions from "./_DemandeEmbaucheActions"
import { ROUTING_BREADCRUMBS_GESTION_STRUCTURE_STAFF_DEMANDES_EMBAUCHE } from "../../gestion/routing/routing-breadcrumbs"
import { useAuth } from "../../../AppContext"
import { CONST_USER_ROLE_DAF, CONST_USER_ROLE_DG, CONST_USER_ROLE_RESPONSABLE_RH, CONST_USER_ROLE_RH } from "@common-constants/*"
import { formatPrenomNom } from "../core/_nameFormat"
import { driveImageUrl } from "../core/_avatar"
import { getFournisseurDocuments } from "../../fournisseur/core/_requests"

const ETAT_BADGE: Record<string, string> = {
     ETAT_BROUILLON: "badge-light-secondary",
     ETAT_SOUMIS_DG: "badge-light-warning",
     ETAT_VALIDE_DG: "badge-light-info",
     ETAT_RECTIF_PAR_DG: "badge-light-danger",
     ETAT_RECTIF_PAR_HR: "badge-light-danger",
     ETAT_VALIDE_HR: "badge-light-primary",
     ETAT_FINALISE: "badge-light-success",
}

// Libellés des documents fournisseur (le endpoint ne renvoie pas toujours typeLabel).
const FOURNISSEUR_DOC_LABELS: Record<string, string> = {
     TYPE_FOURNISSEUR_EXTRAIT_KBIS: "Extrait KBIS",
     TYPE_FOURNISSEUR_ASSURANCE_RC_PRO: "Assurance responsabilité civile pro.",
     TYPE_FOURNISSEUR_VIGILANCE_URSSAF: "Vigilance URSSAF",
     TYPE_FOURNISSEUR_REGULARITE_FISCALE: "Régularité fiscale",
     TYPE_FOURNISSEUR_RIB: "Relevé d'Identité Bancaire (RIB)",
}

const InfoRow = ({ label, value }: { label: string; value?: React.ReactNode }) => (
     <div className="row py-2 border-bottom border-gray-100">
          <div className="col-5 col-md-4 text-muted">{label}</div>
          <div className="col-7 col-md-8 fw-bold">{value === undefined || value === null || value === "" ? <span className="text-muted fw-normal">—</span> : value}</div>
     </div>
)

const DemandeEmbaucheStaffDetails = () => {
     const { id } = useParams<{ id: string }>()
     const demandeId = Number(id)

     const navigate = useNavigate()
     const { currentUser } = useAuth()
     const [demande, setDemande] = React.useState<IPersonnelDemandeEmbaucheModel | null>(null)
     const [loading, setLoading] = React.useState<boolean>(true)
     const [activeDoc, setActiveDoc] = React.useState<IPersonnelDocumentModel | null>(null)
     const [showSuivi, setShowSuivi] = React.useState<boolean>(false)
     const [printing, setPrinting] = React.useState<boolean>(false)
     // Documents du fournisseur (non salarié) : chargés à part (stockés sur l'entité Fournisseur).
     const [fournisseurDocs, setFournisseurDocs] = React.useState<any[]>([])
     const [activeFournisseurDoc, setActiveFournisseurDoc] = React.useState<any | null>(null)

     const load = React.useCallback(() => {
          return personnelDEGetDetailsRequest(demandeId)
               .then(r => setDemande(r.data))
               .catch(() => toast.error("Impossible de charger la demande."))
               .finally(() => setLoading(false))
     }, [demandeId])

     React.useEffect(() => {
          load()
     }, [load])

     // Dossier complet (infos + documents fusionnés) : généré côté serveur, ouvert dans un nouvel onglet pour impression.
     async function handlePrintDossier() {
          setPrinting(true)
          try {
               const r = await personnelDEDossierPdfRequest(demandeId)
               const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }))
               window.open(url, "_blank")
               // Libère l'URL après ouverture (laisse le temps à l'onglet de charger le blob).
               setTimeout(() => URL.revokeObjectURL(url), 60000)
          } catch {
               toast.error("Impossible de générer le dossier PDF.", { autoClose: false })
          } finally {
               setPrinting(false)
          }
     }

     React.useEffect(() => {
          const fid = (demande?.personnel as any)?.fournisseur?.id
          const isNonSal = (demande?.personnel as any)?.statut === "STATUT_NON_SALARIE"
          if (isNonSal && fid) {
               getFournisseurDocuments(fid)
                    .then(r => setFournisseurDocs(r.data ?? []))
                    .catch(() => setFournisseurDocs([]))
          }
     }, [demande])

     if (loading) return <CenteredLoader />
     if (!demande) return <MyAlert type={"danger"} isDismissible={false}><>Demande introuvable.</></MyAlert>

     const p = demande.personnel
     // Champs staff DE récents non typés dans IPersonnelModel (sérialisés côté back) : accès souple.
     const px = (p ?? {}) as any
     const isNonSalarie = px?.statut === "STATUT_NON_SALARIE"
     const fournisseur = px?.fournisseur
     const fournisseurResponsable = px?.fournisseurResponsable
     const documents = (p?.personnelDocuments ?? []).filter(d => !!d.googleDriveID && !d.isDeleted)
     const poste = p?.personnelPostes?.[0]?.poste?.nom
     const etat = demande.etat ?? ""
     const isOwner = demande.requester?.id === currentUser?.personnel?.id
     const roles = currentUser?.roles ?? []
     const isDg = roles.includes(CONST_USER_ROLE_DG)
     const isHr = roles.includes(CONST_USER_ROLE_DAF) || roles.includes(CONST_USER_ROLE_RESPONSABLE_RH) || roles.includes(CONST_USER_ROLE_RH)
     // Édition réservée à l'acteur dont c'est le tour (aligné sur le backend `assertCanEdit`) :
     //  - créateur : brouillon / rectification demandée (il ne voit plus « Corriger » une fois soumise) ;
     //  - DG : uniquement quand la demande lui est soumise (SOUMIS_DG, y compris après un renvoi RH) ;
     //  - RH : uniquement en attente de validation RH (VALIDE_DG).
     // Super-admin / IT (hors créateur) : consultation seule.
     const canEdit = isOwner
          ? ["ETAT_BROUILLON", "ETAT_RECTIF_PAR_DG", "ETAT_RECTIF_PAR_HR"].includes(etat)
          : isDg
            ? etat === "ETAT_SOUMIS_DG"
            : isHr
              ? etat === "ETAT_VALIDE_DG" && !isNonSalarie && !demande.creatorIsHr // non salarié / demande initiée par les RH : étape RH sautée
              : false

     return (
          <>
               <PageTitle breadcrumbs={ROUTING_BREADCRUMBS_GESTION_STRUCTURE_STAFF_DEMANDES_EMBAUCHE}>{formatPrenomNom(p?.prenom, p?.nom)}</PageTitle>

               {/* Zone imprimable : en-tête + toutes les cartes d'informations (cf. demande-embauche-print.css). */}
               <div className="printable-demande">
               <div className="card mb-4">
                    <div className="card-body d-flex flex-column flex-sm-row justify-content-between align-items-start gap-3">
                         <div className="d-flex align-items-center gap-3">
                              <div className="symbol symbol-60px symbol-circle">
                                   {px?.avatarGoogleDriveId ? (
                                        <img src={driveImageUrl(px.avatarGoogleDriveId)} referrerPolicy="no-referrer" alt={formatPrenomNom(p?.prenom, p?.nom)} style={{ objectFit: "cover" }} />
                                   ) : (
                                        <span className="symbol-label bg-light-primary text-primary">
                                             <i className="fas fa-user fs-2" />
                                        </span>
                                   )}
                              </div>
                              <div>
                                   <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                                        <h3 className="mb-0">{formatPrenomNom(p?.prenom, p?.nom)}</h3>
                                        <span className={`badge ${ETAT_BADGE[demande.etat ?? ""] ?? "badge-light"}`}>{demande.etatLabel ?? demande.etat}</span>
                                        <span className={`badge ${isNonSalarie ? "badge-light-info" : "badge-light-primary"}`}>{isNonSalarie ? "Non salarié" : "Salarié"}</span>
                                   </div>
                                   <div className="text-muted">
                                        Demande initiée par {formatPrenomNom(demande.requester?.prenom, demande.requester?.nom) || "—"} le {demande.dateDemande ? moment(demande.dateDemande).format("DD/MM/YYYY") : "—"}
                                   </div>
                              </div>
                         </div>
                         <div className="no-print d-flex flex-column flex-sm-row flex-sm-wrap justify-content-sm-end gap-2 w-100 w-sm-auto">
                              {canEdit && (
                                   <button className="btn btn-sm btn-light-warning w-100 w-sm-auto" onClick={() => navigate(ROUTING_ABSOLUTE_PATH_GESTION_STRUCTURE_STAFF_DEMANDE_EMBAUCHE_EDITER(demande.id))}>
                                        <i className="fas fa-pen me-1" /> {isOwner ? (etat === "ETAT_BROUILLON" ? "Compléter" : "Corriger") : "Éditer"}
                                   </button>
                              )}
                              <DemandeEmbaucheActions demande={demande} onDone={load} onDeleted={() => navigate(ROUTING_ABSOLUTE_PATH_GESTION_STRUCTURE_STAFF_DEMANDES_EMBAUCHE)} />
                              <button className="btn btn-sm btn-light w-100 w-sm-auto" onClick={() => setShowSuivi(true)}>
                                   <i className="fas fa-history me-1" /> Suivi
                              </button>
                              <button className="btn btn-sm btn-light-primary w-100 w-sm-auto" onClick={handlePrintDossier} disabled={printing}>
                                   {printing ? (
                                        <><span className="spinner-border spinner-border-sm me-1" /> Génération…</>
                                   ) : (
                                        <><i className="fas fa-print me-1" /> Imprimer</>
                                   )}
                              </button>
                         </div>
                    </div>
               </div>

               <div className="row">
                    <div className="col-lg-6">
                         <div className="card mb-4">
                              <div className="card-header"><h4 className="card-title">Identité & contact</h4></div>
                              <div className="card-body">
                                   <InfoRow label="Civilité" value={p?.civiliteLabel} />
                                   <InfoRow label="Nom complet" value={formatPrenomNom(p?.prenom, p?.nom)} />
                                   <InfoRow label="Email pro." value={p?.mailProfessionnel} />
                                   <InfoRow label="Tél. pro." value={p?.telProfessionnel && `${p?.telProfessionnelIndicatif?.phoneCodeWithPlusPrefix ?? ""} ${p?.telProfessionnel}`} />
                                   <InfoRow label="Email perso." value={p?.mailPersonnel} />
                                   <InfoRow label="Tél. perso." value={p?.telPersonnel && `${p?.telPersonnelIndicatif?.phoneCodeWithPlusPrefix ?? ""} ${p?.telPersonnel}`} />
                                   {!isNonSalarie && (
                                        <>
                                             <InfoRow label="N° sécurité sociale" value={p?.nSecuriteSociale} />
                                             <InfoRow label="Date de naissance" value={p?.dateNaissance ? moment(p.dateNaissance).format("DD/MM/YYYY") : undefined} />
                                             <InfoRow label="Pays de naissance" value={p?.paysNaissance?.nomFr} />
                                             <InfoRow label="Ville de naissance" value={p?.villeNaissance} />
                                             <InfoRow label="Nationalité étrangère" value={p?.isNationaliteEtrangere ? `Oui${p?.nationalite?.nomFr ? ` — ${p.nationalite.nomFr}` : ""}` : "Non"} />
                                        </>
                                   )}
                              </div>
                         </div>

                         {/* Résidence & contact d'urgence : salarié uniquement. */}
                         {!isNonSalarie && (
                              <div className="card mb-4">
                                   <div className="card-header"><h4 className="card-title">Résidence & contact d'urgence</h4></div>
                                   <div className="card-body">
                                        <InfoRow label="Adresse" value={p?.residenceAdresse} />
                                        <InfoRow label="Ville" value={p?.residenceVilleFrance?.nom ?? p?.residenceVille} />
                                        <InfoRow label="Code postal" value={p?.residenceCodePostal} />
                                        <InfoRow label="Pays" value={p?.residencePays?.nomFr} />
                                        <InfoRow
                                             label="Contact d'urgence"
                                             value={
                                                  p?.personneToContactUrgencePrenom || p?.personneToContactUrgenceNom
                                                       ? `${p?.personneToContactUrgencePrenom ?? ""} ${p?.personneToContactUrgenceNom ?? ""}`.trim()
                                                       : undefined
                                             }
                                        />
                                        <InfoRow label="Tél. contact urgence" value={p?.personneToContactUrgenceTel && `${p?.personneToContactUrgenceTelIndicatif?.phoneCodeWithPlusPrefix ?? ""} ${p?.personneToContactUrgenceTel}`} />
                                   </div>
                              </div>
                         )}

                         {/* Fournisseur : non salarié uniquement. */}
                         {isNonSalarie && (
                              <div className="card mb-4">
                                   <div className="card-header"><h4 className="card-title">Fournisseur</h4></div>
                                   <div className="card-body">
                                        <InfoRow label="Raison sociale" value={fournisseur?.raisonSociale} />
                                        <InfoRow label="Catégorie" value={fournisseur?.typeLabel} />
                                        <InfoRow label="Forme juridique" value={fournisseur?.formeJuridiqueLabel} />
                                        <InfoRow label="SIRET" value={fournisseur?.siret} />
                                        <InfoRow label="Adresse" value={fournisseur?.adresse} />
                                        <InfoRow label="Ville" value={fournisseur?.ville} />
                                        <InfoRow label="Code postal" value={fournisseur?.codePostal} />
                                        <InfoRow label="Pays" value={fournisseur?.pays?.nomFr} />
                                        <InfoRow label="Contact" value={fournisseurResponsable?.prenomNom} />
                                        <InfoRow label="Email contact" value={fournisseurResponsable?.email} />
                                        <InfoRow label="Tél. contact" value={fournisseurResponsable?.tel} />
                                   </div>
                              </div>
                         )}
                    </div>

                    <div className="col-lg-6">
                         <div className="card mb-4">
                              <div className="card-header"><h4 className="card-title">Poste & organisation</h4></div>
                              <div className="card-body">
                                   <InfoRow label="Statut" value={isNonSalarie ? "Non salarié" : p?.statutLabel} />
                                   <InfoRow label="Contrat" value={isNonSalarie ? "Contrat non salarié" : p?.contratLabel} />
                                   <InfoRow label="Métier" value={poste} />
                                   <InfoRow label="Agence" value={p?.agence?.nom} />
                                   <InfoRow label="Entité de rattachement" value={p?.mainEntite?.nom} />
                                   <InfoRow label="Entité de facturation" value={px?.entiteFacturation?.nom} />
                                   <InfoRow label="Date d'entrée" value={p?.dateEntree ? moment(p.dateEntree).format("DD/MM/YYYY") : undefined} />
                                   {isNonSalarie ? (
                                        <InfoRow label="Taux journalier HT" value={px?.tauxJournalierHt != null ? `${px.tauxJournalierHt} €` : undefined} />
                                   ) : (
                                        <>
                                             <InfoRow label="Salaire brut mensuel" value={px?.salaireBrutMensuel != null ? `${px.salaireBrutMensuel} €` : undefined} />
                                             <InfoRow label="Heures travail" value={p?.heuresTravail} />
                                             <InfoRow label="Heures supp." value={p?.heuresTravailSupp} />
                                        </>
                                   )}
                                   <InfoRow label="Profil LinkedIn" value={p?.linkedinUrl ? <a href={p.linkedinUrl} target="_blank" rel="noreferrer">{p.linkedinUrl}</a> : undefined} />
                              </div>
                         </div>

                         <div className="card mb-4">
                              <div className="card-header"><h4 className="card-title">Documents</h4></div>
                              <div className="card-body">
                                   {/* Documents du collaborateur */}
                                   {isNonSalarie && <div className="fw-bold text-gray-700 mb-2">Documents du collaborateur</div>}
                                   {documents.length === 0 ? (
                                        <div className="text-muted">Aucun document.</div>
                                   ) : (
                                        documents.map(doc => (
                                             <div className="d-flex align-items-center justify-content-between py-2 border-bottom border-gray-100" key={doc.id}>
                                                  <span>{doc.typeLabel ?? doc.type}</span>
                                                  <button className="btn btn-sm btn-light-primary" onClick={() => setActiveDoc(doc)}>
                                                       Voir <i className="fas fa-eye ms-1" />
                                                  </button>
                                             </div>
                                        ))
                                   )}

                                   {/* Documents du fournisseur (non salarié) */}
                                   {isNonSalarie && (
                                        <>
                                             <div className="separator separator-dashed my-4" />
                                             <div className="fw-bold text-gray-700 mb-2">Documents du fournisseur</div>
                                             {fournisseurDocs.length === 0 ? (
                                                  <div className="text-muted">Aucun document.</div>
                                             ) : (
                                                  fournisseurDocs.map(doc => (
                                                       <div className="d-flex align-items-center justify-content-between py-2 border-bottom border-gray-100" key={doc.id}>
                                                            <span>{doc.typeLabel ?? FOURNISSEUR_DOC_LABELS[doc.type] ?? doc.type}</span>
                                                            <button className="btn btn-sm btn-light-primary" onClick={() => setActiveFournisseurDoc(doc)}>
                                                                 Voir <i className="fas fa-eye ms-1" />
                                                            </button>
                                                       </div>
                                                  ))
                                             )}
                                        </>
                                   )}
                              </div>
                         </div>
                    </div>
               </div>
               </div>
               {/* fin .printable-demande */}

               {showSuivi && (
                    <MyModal title={"Historique de la demande"} show={showSuivi} onCloseCallback={() => setShowSuivi(false)}>
                         <div className="timeline">
                              {(demande.suivis ?? []).length === 0 ? (
                                   <div className="text-muted">Aucun événement.</div>
                              ) : (
                                   [...(demande.suivis ?? [])].reverse().map(s => (
                                        <div className="d-flex align-items-start mb-4" key={s.id}>
                                             <span className="bullet bullet-vertical h-40px bg-primary me-3" />
                                             <div>
                                                  <div className="fw-bold">{s.typeLabel ?? s.type}</div>
                                                  <div className="text-muted fs-7">
                                                       {s.date ? moment(s.date).format("DD/MM/YYYY HH:mm") : ""}
                                                       {s.personnel ? ` — ${s.personnel.prenomNom ?? ""}` : ""}
                                                  </div>
                                                  {s.commentaire && <div className="mt-1 p-2 bg-light-warning rounded fs-7">{s.commentaire}</div>}
                                             </div>
                                        </div>
                                   ))
                              )}
                         </div>
                    </MyModal>
               )}

               {activeDoc && activeDoc.googleDriveID && (
                    <MyGoogleDriveFileViewerInModal
                         googleDriveID={activeDoc.googleDriveID}
                         googleDriveMimetype={activeDoc.googleDriveMimeType ?? ""}
                         modalTitle={<>{activeDoc.typeLabel ?? "Document"}</>}
                         handleCloseModal={() => setActiveDoc(null)}
                    />
               )}

               {activeFournisseurDoc && activeFournisseurDoc.googleDriveId && (
                    <MyGoogleDriveFileViewerInModal
                         googleDriveID={activeFournisseurDoc.googleDriveId}
                         googleDriveMimetype={activeFournisseurDoc.googleDriveMimeType ?? ""}
                         modalTitle={<>{activeFournisseurDoc.typeLabel ?? "Document fournisseur"}</>}
                         handleCloseModal={() => setActiveFournisseurDoc(null)}
                    />
               )}
          </>
     )
}

export default DemandeEmbaucheStaffDetails
